import { useTranslation } from 'react-i18next';
import { match } from 'ts-pattern';

import { Button, Dialog } from '~/components';

import type { InvoiceConfirmationDialog as Props } from './dialogs.types';

import { EditEntryNumberForm } from './EditEntryNumberForm';
import { useEntryNumberForm } from './EditEntryNumberForm.hooks';

export const InvoiceConfirmationDialog = (props: Props) => {
  return (
    <Dialog.Body>
      <InvoiceConfirmationDialogContent {...props} />
    </Dialog.Body>
  );
};

const InvoiceConfirmationDialogContent = ({
  allowsAutomaticApproval,
  children,
  disabled,
  isEdit,
  numberingSeries,
  onConfirm,
  onSaveAndExit,
}: Props) => {
  const { fcEntryNumber, form } = useEntryNumberForm(numberingSeries);
  const nextNumber = form.getValues('nextNumber') + 1;

  const { t } = useTranslation(['common', 'invoices']);

  const variant = allowsAutomaticApproval ? 'automaticApproval' : isEdit ? 'edit' : 'create';

  return (
    <>
      <Dialog.Header
        title={match(variant)
          .with('automaticApproval', () => t('invoices:createEdit.automaticApprovalModal.title'))
          .with('edit', () => t('invoices:createEdit.confirmation.titleEdit'))
          .with('create', () => t('invoices:createEdit.confirmation.title'))
          .exhaustive()}
      />

      {children}

      {!isEdit && numberingSeries && (
        <EditEntryNumberForm fcEntryNumber={fcEntryNumber} form={form} numberingSeries={numberingSeries} type="invoice" />
      )}

      <Dialog.Actions>
        {match(variant)
          .with('automaticApproval', () => (
            <>
              <Button
                disabled={disabled}
                emphasis="secondary"
                onClick={() => onConfirm({ approvalType: 'manual', fcEntryNumber, nextNumber })}
              >
                {t('invoices:createEdit.automaticApprovalModal.manual')}
              </Button>
              <Button disabled={disabled} icon="Send" onClick={() => onConfirm({ approvalType: 'automatic', fcEntryNumber, nextNumber })}>
                {t('invoices:createEdit.automaticApprovalModal.automatic')}
              </Button>
            </>
          ))
          .with('edit', () => (
            <>
              <Button disabled={disabled} emphasis="secondary" icon="Save" onClick={() => onSaveAndExit()}>
                {t('invoices:createEdit.confirmation.submit.saveAndExit')}
              </Button>
              <Button
                disabled={disabled}
                icon="Send"
                onClick={() => onConfirm({ approvalType: 'manual', fcEntryNumber: null, nextNumber: null })}
              >
                {t('invoices:createEdit.confirmation.submit.edit')}
              </Button>
            </>
          ))
          .with('create', () => (
            <Button icon="Send" loading={disabled} onClick={() => onConfirm({ approvalType: 'manual', fcEntryNumber, nextNumber })}>
              {t('invoices:createEdit.confirmation.submit.create')}
            </Button>
          ))
          .exhaustive()}
      </Dialog.Actions>
    </>
  );
};
